import { useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Eye, Users, Inbox, CalendarCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatCard, Loading } from "@/components/common";
import { useFetch, fmtDate } from "@/lib/api";
import { STATUS_LABEL } from "@/pages/biz/Listings";

function Table({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="performance-table">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <th className="py-2 pr-4">Listing</th><th className="py-2 pr-4 w-[30%]">Views</th><th className="py-2 pr-4 text-right">Visitors</th>
          <th className="py-2 pr-4 text-right">Enquiries</th><th className="py-2 pr-4 text-right">Viewings</th><th className="py-2 text-right">Lead rate</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-b border-slate-100" data-testid={`performance-row-${r.id}`}>
            <td className="py-2.5 pr-4"><Link to={`/app/listings/${r.id}`} className="font-semibold hover:underline">{r.title}</Link><div className="text-xs text-slate-500">{STATUS_LABEL[r.status]} · {r.channel === "letting" ? "To let" : "For sale"}</div></td>
            <td className="py-2.5 pr-4"><div className="flex items-center gap-2"><div className="h-2 rounded-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${(r.views / max) * 100}%`, minWidth: r.views ? 4 : 0 }} /><span className="font-semibold">{r.views}</span></div></td>
            <td className="py-2.5 pr-4 text-right">{r.visitors}</td>
            <td className="py-2.5 pr-4 text-right">{r.enquiries}</td>
            <td className="py-2.5 pr-4 text-right">{r.viewings}{r.confirmed_viewings > 0 && <span className="text-xs text-slate-500"> ({r.confirmed_viewings} ok)</span>}</td>
            <td className="py-2.5 text-right font-semibold">{r.views ? `${r.conversion}%` : "—"}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export function ListingPerformance() {
  const [days, setDays] = useState("30");
  const { data } = useFetch(`/biz/listing-analytics?days=${days}`);
  return (
    <section className="mb-10" data-testid="listing-performance">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-extrabold">Performance</h2>
        <Select value={days} onValueChange={setDays}><SelectTrigger className="w-40" data-testid="performance-days"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="7">Last 7 days</SelectItem><SelectItem value="30">Last 30 days</SelectItem><SelectItem value="90">Last 90 days</SelectItem><SelectItem value="365">Last 12 months</SelectItem></SelectContent></Select>
      </div>
      {!data ? <Loading /> : <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard label="Page views" value={data.totals.views} hint="One per visitor per day" icon={Eye} testid="perf-views" />
          <StatCard label="Visitors" value={data.totals.visitors} icon={Users} testid="perf-visitors" />
          <StatCard label="Enquiries" value={data.totals.enquiries} icon={Inbox} tone="green" testid="perf-enquiries" />
          <StatCard label="Viewing requests" value={data.totals.viewings} hint={`${data.totals.confirmed_viewings} confirmed`} icon={CalendarCheck} tone="green" testid="perf-viewings" />
        </div>
        <div className="panel mb-4 h-56" data-testid="performance-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily} margin={{ left: -20, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="day" tickFormatter={(d) => fmtDate(d).slice(0, 6)} tick={{ fontSize: 11 }} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtDate} />
              <Area type="monotone" dataKey="views" name="Views" stroke="#047857" fill="#04785722" strokeWidth={2} />
              <Area type="monotone" dataKey="enquiries" name="Enquiries" stroke="#F59E0B" fill="#F59E0B22" strokeWidth={2} />
              <Area type="monotone" dataKey="viewings" name="Viewing requests" stroke="#0EA5E9" fill="#0EA5E922" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {data.listings.length > 0 && <div className="panel"><Table rows={data.listings} /></div>}
      </>}
    </section>
  );
}
